import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useUpdatePlaylist } from '../hooks/usePlaylists';
import type { AudioPlaylistDto } from '../types';

const USE_CASES = ['cafe', 'restaurant', 'bar', 'shop', 'hotel', 'gym', 'spa', 'office', 'other'];

interface EditPlaylistModalProps {
  playlist: AudioPlaylistDto;
  isOpen: boolean;
  onClose: () => void;
}

export function EditPlaylistModal({ playlist, isOpen, onClose }: EditPlaylistModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdatePlaylist(playlist.id);

  const [name, setName] = useState(playlist.name);
  const [description, setDescription] = useState(playlist.description ?? '');
  const [useCase, setUseCase] = useState(playlist.targetUseCase ?? '');

  useEffect(() => {
    if (isOpen) {
      setName(playlist.name);
      setDescription(playlist.description ?? '');
      setUseCase(playlist.targetUseCase ?? '');
    }
  }, [isOpen, playlist]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      await update.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        targetUseCase: useCase || undefined,
      });
      toast({ status: 'success', title: t('playlists.saved'), duration: 2000 });
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        title: t('common.error', 'Something went wrong'),
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('playlists.edit')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>{t('playlists.name')}</FormLabel>
              <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('playlists.description')}</FormLabel>
              <Textarea
                value={description}
                maxLength={1000}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('playlists.useCase')}</FormLabel>
              <Select
                placeholder={t('playlists.useCaseNone')}
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
              >
                {USE_CASES.map((u) => (
                  <option key={u} value={u}>
                    {t(`playlists.useCases.${u}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={update.isPending}
            isDisabled={!name.trim()}
          >
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditPlaylistModal;
